import React, { useCallback, useEffect, useMemo } from 'react';
import { useGetCalculationSkusQuery } from '../../../../../../../Calculations/apiCalculationsSkus';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../../../../store';
import { AvailableModals } from '../../../../../../../Modals/modalSlice';
import { useTemplateOrCalculation } from '../../../../../../../../hooks/useTemplateOrCalculation';
import ProductGroupSelector from './ProductGroupSelector';
import { Label, Row } from '@ydistri/ds';
import { Alert, Space } from 'antd';
import { useGetPickingPositionsWithinProductGroupQuery } from '../../../../../../apiCalculationDetail';
import { skipToken } from '@reduxjs/toolkit/query';
import PickingLineProductGroupTable from './PickingLineProductGroupTable';
import {
  emptyPickingLineHalf,
  PickingLineHalf,
  PickingLineProductGroupInfoRow,
} from './pickingLineProductGroupInfoLib';
import { ProductGroupResponse } from '@ydistri/api-sdk';
import Identifications from '../../../../../../../../components/global/Identifications/Identifications';
import { SYSTEM_ID_LABEL } from '../../../../../../../../components/global/_constants';

const PickingLineProductGroupInfo: React.FC = () => {
  const calculationData = useTemplateOrCalculation();
  const calculationId = calculationData.id;
  const modalData = useSelector(
    (state: ReduxState) => state.modal.modalData[AvailableModals.PICKING_LINE_PRODUCT_GROUPS],
  );
  const { sourceSkuId, targetSkuId, sourceStore, targetStore } = modalData ?? {
    sourceSkuId: 0,
    targetSkuId: 0,
    sourceStore: undefined,
    targetStore: undefined,
  };
  const [productGroup, setProductGroup] = React.useState<ProductGroupResponse | null>(null);
  const productGroupId = productGroup?.id ?? null;

  const { data: skuConfiguration, isFetching: isFetchingSkuConfiguration } =
    useGetCalculationSkusQuery({
      skuId: sourceSkuId,
      calculationId,
    });
  const {
    data: pickingPositions,
    isFetching: isFetchingPickingPositions,
    isError: isErrorPickingPositions,
  } = useGetPickingPositionsWithinProductGroupQuery(
    productGroupId && sourceSkuId && targetSkuId
      ? {
          calculationId,
          sourceSkuId,
          targetSkuId,
          productGroupId,
        }
      : skipToken,
  );

  const routePickingPositions = useMemo(() => pickingPositions ?? [], [pickingPositions]);

  const productGroups = useMemo(
    () => skuConfiguration?.productGroups ?? [],
    [skuConfiguration?.productGroups],
  );

  useEffect(() => {
    if (!productGroup && productGroups.length > 0) {
      setProductGroup(productGroups[0]);
    }
  }, [productGroup, productGroups]);

  const selectedPickingLine = useMemo(
    () =>
      routePickingPositions.find(
        pp => pp.sourceSkuId === sourceSkuId && pp.targetSkuId === targetSkuId,
      ),
    [routePickingPositions, sourceSkuId, targetSkuId],
  );

  const product = selectedPickingLine?.product;

  const data: PickingLineProductGroupInfoRow[] = useMemo(() => {
    const productData: Record<number, PickingLineProductGroupInfoRow | undefined> = {};
    const productIds = new Set<number>();

    routePickingPositions.forEach(p => {
      const pp = { ...p };

      const productId = pp.product.id;
      productIds.add(productId);

      productData[productId] ??= {
        product: pp.product,
        // eslint-disable-next-line typescript/no-unnecessary-condition -- parameters may be undefined at runtime
        parameters: pp.parameters ?? [],
        isOpenedPickingLine: false,
        hasRouteQuantityChange: false,
        source: { ...emptyPickingLineHalf },
        target: { ...emptyPickingLineHalf },
      };

      productData[productId].source.otherRoutes = -pp.totalQuantityFromSourceSku;
      productData[productId].target.otherRoutes = pp.totalQuantityToTargetSku;

      if (pp.quantity) {
        productData[productId].hasRouteQuantityChange = true;

        productData[productId].source.thisRoute = -pp.quantity;
        productData[productId].target.thisRoute = pp.quantity;

        productData[productId].source.otherRoutes += pp.quantity;
        productData[productId].target.otherRoutes -= pp.quantity;

        if (sourceSkuId === pp.sourceSkuId && targetSkuId === pp.targetSkuId) {
          productData[productId].isOpenedPickingLine = true;
        }
      }

      productData[productId].source.skuId = pp.sourceSkuId ?? undefined;
      // eslint-disable-next-line typescript/no-unnecessary-condition -- sourceIsInPotential may be undefined at runtime
      productData[productId].source.isInPotential = pp.sourceIsInPotential ?? false;
      productData[productId].source.availableSupplyNow = pp.sourceAvailableSupply;
      productData[productId].source.minLayer = pp.sourceMinLayerQuantity;
      productData[productId].source.incoming = pp.sourceIncomingQuantity;
      productData[productId].source.outgoing = pp.sourceOutgoingQuantity;
      productData[productId].source.openPurchaseOrders = pp.sourceOpenPurchaseOrdersQuantity;

      productData[productId].target.skuId = pp.targetSkuId ?? undefined;
      // eslint-disable-next-line typescript/no-unnecessary-condition -- targetIsInPotential may be undefined at runtime
      productData[productId].target.isInPotential = pp.targetIsInPotential ?? false;
      productData[productId].target.availableSupplyNow = pp.targetAvailableSupply;
      productData[productId].target.minLayer = pp.targetMinLayerQuantity;
      productData[productId].target.incoming = pp.targetIncomingQuantity;
      productData[productId].target.outgoing = pp.targetOutgoingQuantity;
      productData[productId].target.openPurchaseOrders = pp.targetOpenPurchaseOrdersQuantity;
    });

    const processPickingLineHalf = (h: PickingLineHalf) => {
      h.availableSupplyNowAfterMath =
        h.availableSupplyNow + h.openPurchaseOrders + h.incoming - h.outgoing;
      h.finalSupply = h.availableSupplyNowAfterMath + h.thisRoute + h.otherRoutes;
      h.showCurrentSupplyInfo = h.openPurchaseOrders !== 0 || h.incoming !== 0 || h.outgoing !== 0;
    };

    [...productIds].forEach(pid => {
      if (!productData[pid]) return;
      processPickingLineHalf(productData[pid].source);
      processPickingLineHalf(productData[pid].target);
    });

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- data should be fine
    return Object.values(productData).filter(Boolean) as PickingLineProductGroupInfoRow[];
  }, [routePickingPositions, sourceSkuId, targetSkuId]);

  const setProductGroupById = useCallback(
    (pgId: number) => {
      const g = productGroups.find(pg => pg.id === pgId);
      setProductGroup(g ?? null);
    },
    [productGroups],
  );

  const ids = useMemo(() => {
    if (!productGroup) return [];
    return [
      { label: 'ID', value: productGroup.customerId },
      { label: SYSTEM_ID_LABEL, value: productGroup.id.toString() },
    ];
  }, [productGroup]);

  return (
    <div>
      {productGroupId && (
        <Space direction="vertical" size="large">
          <Space>
            <Label>Product group: </Label>
            <ProductGroupSelector
              value={productGroupId}
              setValue={setProductGroupById}
              productGroups={productGroups}
            />
            <Row $gap="2rem">
              <Identifications items={ids} />
            </Row>
          </Space>
          {isErrorPickingPositions && (
            <Alert type="error" message="Failed to load picking positions" showIcon />
          )}
          <PickingLineProductGroupTable
            data={data}
            loading={isFetchingPickingPositions || isFetchingSkuConfiguration}
            selectedProductGroupId={productGroupId}
            productGroups={productGroups}
            product={product}
            sourceStore={sourceStore}
            targetStore={targetStore}
          />
        </Space>
      )}
    </div>
  );
};

export default PickingLineProductGroupInfo;
